import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

import { EunerdMark } from "@/components/eunerd-mark";
import { cn } from "@/lib/utils";
import { signInWithGoogle } from "@/lib/auth";
import { firebaseConfigured } from "@/lib/firebase";
import { useAuth, isAuthenticated } from "@/stores/auth";

interface LocationState {
  from?: { pathname: string };
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { hydrated } = useAuth();
  const [loading, setLoading] = useState(false);

  if (hydrated && isAuthenticated()) {
    const state = location.state as LocationState | null;
    return <Navigate to={state?.from?.pathname ?? "/"} replace />;
  }

  const handleGoogle = async () => {
    if (!firebaseConfigured) {
      toast.error("Login indisponível", {
        description: "Configure as variáveis VITE_FIREBASE_* no build.",
      });
      return;
    }
    setLoading(true);
    try {
      const user = await signInWithGoogle();
      toast.success(`Bem-vindo, ${user.name ?? user.email}`);
      navigate("/", { replace: true });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Erro ao entrar";
      toast.error("Não foi possível entrar", { description: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="relative min-h-dvh flex items-center justify-center px-6 overflow-hidden bg-background">
      {/* Background ambient gradients (Apple-style) */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 -left-32 h-[480px] w-[480px] rounded-full bg-primary-300/20 blur-3xl" />
        <div className="absolute -bottom-32 -right-32 h-[520px] w-[520px] rounded-full bg-primary-200/30 blur-3xl" />
        <div className="absolute top-1/2 left-1/2 h-[320px] w-[320px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary-100/40 blur-3xl" />
      </div>

      <div className="relative animate-fade-in-up">
        <div
          className={cn(
            "flex flex-col items-center gap-10",
            "rounded-3xl border border-white/40 bg-white/70 backdrop-blur-2xl",
            "shadow-[0_20px_70px_-15px_rgba(15,23,42,0.25)]",
            "px-12 py-14 w-[420px] max-w-[calc(100vw-3rem)]",
            "dark:bg-card/60 dark:border-white/5 dark:shadow-[0_20px_70px_-15px_rgba(0,0,0,0.6)]",
          )}
        >
          <EunerdMark className="h-16 w-auto drop-shadow-sm" />

          <button
            type="button"
            onClick={handleGoogle}
            disabled={loading}
            aria-label="Entrar com Google"
            className={cn(
              "group relative w-full inline-flex items-center justify-center gap-3",
              "rounded-2xl border border-slate-200/80 bg-white",
              "px-6 py-3.5 text-[15px] font-medium text-slate-700",
              "shadow-[0_1px_2px_rgba(15,23,42,0.06),0_4px_12px_-4px_rgba(15,23,42,0.10)]",
              "transition-all duration-200 ease-spring",
              "hover:shadow-[0_2px_4px_rgba(15,23,42,0.08),0_12px_28px_-8px_rgba(15,23,42,0.18)]",
              "hover:-translate-y-0.5",
              "active:translate-y-0 active:scale-[0.98] active:shadow-[0_1px_2px_rgba(15,23,42,0.06)]",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2",
              "disabled:opacity-60 disabled:pointer-events-none",
              "dark:bg-slate-900 dark:border-white/10 dark:text-slate-100",
            )}
          >
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin text-primary-600" aria-hidden />
            ) : (
              <GoogleIcon className="h-5 w-5 transition-transform group-hover:scale-110" aria-hidden />
            )}
            <span>{loading ? "Entrando..." : "Continuar com Google"}</span>
          </button>
        </div>
      </div>
    </main>
  );
}

function GoogleIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" {...props} aria-hidden>
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.07z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.12c-.22-.66-.35-1.36-.35-2.12s.13-1.46.35-2.12V7.04H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.96l3.66-2.84z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.04l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"
      />
    </svg>
  );
}
